import type { SearchFilters } from '../../ExploreListings/types';

/** Small filter objects need value equality, including nested range objects. */
function filterKey(value: unknown): string {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const object = value as Record<string, unknown>;
    return JSON.stringify(Object.keys(object).sort().map((key) => [key, filterKey(object[key])]));
  }
  return JSON.stringify(value);
}

export function mergeAppliedFilters(previous: SearchFilters, updates: Partial<SearchFilters>): SearchFilters {
  const next = { ...previous, ...updates };
  return filterKey(previous) === filterKey(next) ? previous : next;
}

/** Invalidate immediately, even while the replacement is still debouncing. */
export function createDemandRequest<T>(
  fetchDemand: (filters: SearchFilters, signal: AbortSignal) => Promise<T>,
  apply: (data: T) => void,
  onError: (error: unknown) => void,
) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let controller: AbortController | undefined;
  let revision = 0;
  let disposed = false;
  return {
    load(filters: SearchFilters) {
      if (disposed) return;
      const current = ++revision;
      clearTimeout(timer);
      controller?.abort();
      timer = setTimeout(async () => {
        const request = new AbortController();
        controller = request;
        try {
          const data = await fetchDemand(filters, request.signal);
          if (!disposed && current === revision) apply(data);
        } catch (error) {
          if (!disposed && current === revision && !request.signal.aborted) onError(error);
        } finally {
          if (controller === request) controller = undefined;
        }
      }, 300);
    },
    dispose() {
      disposed = true;
      ++revision;
      clearTimeout(timer);
      controller?.abort();
    },
  };
}
